import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Check, Loader2, Tag, ArrowRight } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BookingModal } from "@/components/BookingModal";
import { photoUrl } from "@/components/LocationParts";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { CommunitySignup } from "@/components/CommunitySignup";
import { api, formatApiError, trackEvent } from "@/lib/apiClient";
import { BOOK, ONE_TO_ONE } from "@/content";


function Locations({ locations }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {locations.map((l, i) => {
        const img = photoUrl(l);
        const n = l.timetable?.length || 0;
        return (
          <Reveal key={l.id} delay={i * 0.06}>
            <Link to={`/book/${l.id}`} data-testid={`location-${l.id}`}
              className="group block h-full rounded-2xl border border-white/8 bg-ink-800 overflow-hidden hover:border-cyan/50 transition-colors">
              <div className="relative aspect-[16/10] overflow-hidden bg-ink-700">
                {img ? (
                  <img src={img} alt={l.studio || l.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                  <div className="absolute inset-0 mesh-top opacity-70 flex items-center justify-center"><MapPin size={34} className="text-cyan/60" /></div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-ink-800 to-transparent" />
              </div>
              <div className="p-5">
                <p className="font-display text-xl font-bold text-white">{l.name}</p>
                <p className="text-mist text-sm">{l.studio || "Studio to be confirmed"}</p>
                {l.address && <p className="text-mist/80 text-xs mt-1">{l.address}</p>}
                <div className="flex items-center justify-between mt-4">
                  <span className="text-cyan/80 text-xs">{n ? `${n} class${n === 1 ? "" : "es"} running` : "Times being confirmed"}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-electric rounded-full px-4 py-2 group-hover:bg-[#1a5cff] transition-colors">
                    View & book <ArrowRight size={13} />
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>
        );
      })}
    </div>
  );
}

function singleCard(tpl, loc) {
  const prices = [...new Set(loc.timetable.map((t) => Number(t.price ?? 12)))].sort((a, b) => a - b);
  if (!prices.length) return null;
  return { ...tpl, id: `single-${loc.id}`, single: true, location_id: loc.id,
    price_label: prices.length > 1 ? `From £${prices[0]}` : `£${prices[0]}` };
}

function bundleGroups(bundles, locations) {
  const tpl = bundles.find((b) => b.id === "single") || { name: "Single class", description: "One class, one time." };
  const general = bundles.filter((b) => !b.location_id && b.id !== "single");
  const groups = locations.filter((l) => !l.external).map((l) => ({
    id: l.id, name: l.name,
    items: [singleCard(tpl, l), ...bundles.filter((b) => b.location_id === l.id)].filter(Boolean),
  })).filter((g) => g.items.length);
  return [...(general.length ? [{ id: "all", name: "All locations", items: general }] : []), ...groups];
}

function Bundles({ bundles, locations, onBuy, onSingle }) {
  return (
    <div className="space-y-14">
      {bundleGroups(bundles, locations).map((g) => (
        <section key={g.id} data-testid={`bundle-group-${g.id}`}>
          {g.name && <h3 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-2"><MapPin size={20} className="text-cyan" /> {g.name}</h3>}
          <BundleCards bundles={g.items} onBuy={onBuy} onSingle={onSingle} />
        </section>
      ))}
      <BundleInfo />
    </div>
  );
}

function BundleCards({ bundles, onBuy, onSingle }) {
  return (
      <div className="grid md:grid-cols-3 gap-6">
        {bundles.map((b, i) => {
          const featured = b.id === "bundle-5";
          return (
            <Reveal key={b.id} delay={i * 0.08}>
              <div data-testid={`bundle-${b.id}`} className={`relative h-full rounded-3xl border p-8 flex flex-col ${featured ? "border-cyan/40 glass glow-blue" : "border-white/8 bg-ink-800"}`}>
                {featured && <span className="absolute top-5 right-5 label text-cyan">Popular</span>}
                <Tag size={20} className="text-cyan" />
                <h3 className="font-display text-2xl font-bold text-white mt-4">{b.name}</h3>
                <p className="font-display text-5xl font-black text-white mt-3">{b.price_label || `£${b.price}`}</p>
                {!b.single && <p className="text-mist text-sm mt-2">{b.valid_label}</p>}
                {b.location_name && <p className="text-cyan text-xs mt-2" data-testid={`bundle-location-${b.id}`}>Valid at {b.location_name} only</p>}
                <p className="text-ice/70 text-sm mt-5 flex-1">{b.description}</p>
                <button data-testid={`buy-${b.id}`} onClick={() => (b.single ? onSingle(b.location_id) : onBuy(b))} className="book-cta w-full justify-center mt-7">
                  {b.single ? "Book a single class" : "Get this bundle"}
                </button>
              </div>
            </Reveal>
          );
        })}
      </div>
  );
}

function BundleInfo() {
  return (
      <div className="rounded-2xl border border-white/8 bg-ink-800/60 p-8">
        <h4 className="font-display text-xl font-bold text-white">How it works</h4>
        <p className="text-ice/70 mt-3 max-w-2xl">{BOOK.bundleHow}</p>
        <h4 className="font-display text-lg font-bold text-white mt-8">Terms & conditions</h4>
        <ul className="mt-4 space-y-2">
          {BOOK.bundleTerms.map((t, i) => (
            <li key={i} className="flex items-start gap-3 text-ice/70 text-sm"><Check size={16} className="text-cyan mt-0.5 shrink-0" /> {t}</li>
          ))}
        </ul>
      </div>
  );
}

function OneToOne() {
  const ic = "w-full rounded-lg bg-ink-900 border border-white/12 px-3.5 py-2.5 text-ice placeholder:text-mist focus:border-cyan/60 outline-none text-sm";
  const [f, setF] = useState({ option: "bundle-5", name: "", email: "", phone: "", setting: "Either", message: "" });
  const [agreed, setAgreed] = useState(false);
  const [state, setState] = useState("form");
  const [error, setError] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) { setError("Please agree to the 1:1 terms to continue."); return; }
    setState("loading"); setError("");
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "one_to_one", plan: f.option, name: f.name, email: f.email, phone: f.phone,
        setting: f.setting, note: f.message, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); setState("form"); }
  };

  return (
    <div data-testid="one-to-one">
      <div className="grid md:grid-cols-3 gap-6">
        {ONE_TO_ONE.options.map((o, i) => (
          <Reveal key={o.id} delay={i * 0.08}>
            <div data-testid={`one-to-one-option-${o.id}`} className={`relative h-full rounded-3xl border p-7 flex flex-col ${o.featured ? "border-cyan/40 glass glow-blue" : "border-white/8 bg-ink-800"}`}>
              {o.featured && <span className="absolute top-5 right-5 label text-cyan">Popular</span>}
              <Tag size={20} className="text-cyan" />
              <h3 className="font-display text-xl font-bold text-white mt-4">{o.name}</h3>
              <p className="font-display text-5xl font-black text-white mt-3">£{o.price}</p>
              <p className="text-ice/75 text-sm mt-4 flex-1">{o.includes}</p>
              {o.valid !== "—" && <p className="text-mist text-xs mt-3">Valid {o.valid}</p>}
              <button data-testid={`one-to-one-choose-${o.id}`} onClick={() => { set("option", o.id); document.getElementById("one-to-one-form")?.scrollIntoView({ behavior: "smooth" }); }}
                className="book-cta w-full justify-center mt-6">Request this</button>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-10 mt-14">
        <div>
          <p className="text-ice/80 leading-relaxed">{ONE_TO_ONE.intro}</p>
          <p className="text-white font-medium mt-6">{ONE_TO_ONE.focusIntro}</p>
          <ul className="mt-3 space-y-2">
            {ONE_TO_ONE.focuses.map((x, i) => <li key={i} className="flex items-start gap-3 text-ice/70 text-sm"><Check size={16} className="text-cyan mt-0.5 shrink-0" /> {x}</li>)}
          </ul>
          <h4 className="font-display text-lg font-bold text-white mt-8">Where sessions take place</h4>
          <p className="text-ice/70 text-sm mt-2">{ONE_TO_ONE.where}</p>
          <h4 className="font-display text-lg font-bold text-white mt-6">How it works</h4>
          <p className="text-ice/70 text-sm mt-2">{ONE_TO_ONE.how}</p>
          <h4 className="font-display text-lg font-bold text-white mt-6">Cancellations</h4>
          <p className="text-ice/70 text-sm mt-2">{ONE_TO_ONE.cancellations}</p>
          <h4 className="font-display text-lg font-bold text-white mt-6">Your right to cancel</h4>
          <p className="text-ice/70 text-sm mt-2">{ONE_TO_ONE.cooling}</p>
          <h4 className="font-display text-lg font-bold text-white mt-6">Terms</h4>
          <ul className="mt-3 space-y-2">
            {ONE_TO_ONE.terms.map((x, i) => <li key={i} className="flex items-start gap-3 text-ice/70 text-sm"><Check size={16} className="text-cyan mt-0.5 shrink-0" /> {x}</li>)}
          </ul>
        </div>

        <div id="one-to-one-form">
          <div className="rounded-3xl glass p-7 sticky top-28">
            {state === "done" ? (
              <div className="text-center py-8" data-testid="one-to-one-done">
                <div className="mx-auto h-16 w-16 rounded-full bg-cyan/15 flex items-center justify-center mb-5"><Check className="text-cyan" size={30} /></div>
                <h3 className="font-display text-2xl font-bold text-white">Request sent.</h3>
                <p className="text-ice/75 mt-3">Thanks — I'll be in touch to arrange your session time, place and payment.</p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <span className="label text-cyan">Request a 1:1</span>
                <h3 className="font-display text-2xl font-bold text-white mt-2">Start your sessions</h3>
                <div className="space-y-3 mt-5">
                  <div>
                    <label className="label block mb-1.5">Option</label>
                    <select data-testid="one-to-one-option-select" className={ic} value={f.option} onChange={(e) => set("option", e.target.value)}>
                      {ONE_TO_ONE.options.map((o) => <option key={o.id} value={o.id}>{o.name} — £{o.price}</option>)}
                    </select>
                  </div>
                  <input data-testid="one-to-one-name" required placeholder="Your name" className={ic} value={f.name} onChange={(e) => set("name", e.target.value)} />
                  <input data-testid="one-to-one-email" required type="email" placeholder="Email" className={ic} value={f.email} onChange={(e) => set("email", e.target.value)} />
                  <input data-testid="one-to-one-phone" placeholder="Phone (optional)" className={ic} value={f.phone} onChange={(e) => set("phone", e.target.value)} />
                  <div>
                    <label className="label block mb-1.5">Preferred setting</label>
                    <select data-testid="one-to-one-setting" className={ic} value={f.setting} onChange={(e) => set("setting", e.target.value)}>
                      <option>Either</option><option>Indoors (studio)</option><option>Outdoors</option>
                    </select>
                  </div>
                  <textarea data-testid="one-to-one-message" required rows={4} placeholder={ONE_TO_ONE.requestPrompt} className={ic} value={f.message} onChange={(e) => set("message", e.target.value)} />
                  <label className="flex items-start gap-2.5 text-sm text-ice/90 cursor-pointer">
                    <input type="checkbox" data-testid="one-to-one-agree" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="accent-cyan h-4 w-4 mt-0.5 shrink-0" />
                    <span>I have read and agree to the 1:1 terms above, including cancellations and the validity period.</span>
                  </label>
                </div>
                {error && <p className="text-red-300 text-sm mt-3" data-testid="one-to-one-error">{error}</p>}
                <button data-testid="one-to-one-submit" type="submit" disabled={state === "loading" || !agreed} className="book-cta w-full justify-center mt-5 disabled:opacity-50">
                  {state === "loading" ? <Loader2 className="animate-spin" size={16} /> : "Continue to payment"}
                </button>
                <p className="text-mist text-[0.7rem] text-center mt-3">You'll be taken to secure Stripe checkout. Your notes reach Alexia, who arranges your session times and place with you after purchase.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Book() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [tab, setTab] = useState(["bundles", "one-to-one"].includes(params.get("tab")) ? params.get("tab") : "locations");
  const [locations, setLocations] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [modal, setModal] = useState({ open: false });
  const [showUpsell, setShowUpsell] = useState(false);

  useEffect(() => {
    trackEvent("book_start");
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/bundles").then((r) => setBundles(r.data)).catch(() => {});
  }, []);

  const buyBundle = (bundle) => setModal({ open: true, mode: "bundle", bundle });

  const closeModal = () => setModal({ open: false });

  return (
    <Layout page="book">
      <section className="relative pt-40 pb-14 overflow-hidden">
        <div className="absolute inset-0 mesh-top" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <span className="label">Book</span>
          <h1 className="font-display font-black text-white tracking-tighter mt-4 leading-[0.95]" style={{ fontSize: "clamp(3rem, 10vw, 8rem)" }}>Come and be part of it.</h1>
          <p className="text-ice/80 text-lg max-w-3xl mt-6 leading-relaxed">{BOOK.body}</p>
        </div>
      </section>

      <section className="relative">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="inline-flex rounded-full border border-white/10 bg-ink-800 p-1.5" data-testid="book-tabs">
            {[{ k: "locations", l: "Locations" }, { k: "bundles", l: "Bundles" }, { k: "one-to-one", l: "1:1 Sessions" }].map((t) => (
              <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
                className="relative rounded-full px-7 py-2.5 text-sm font-medium transition-colors">
                {tab === t.k && <motion.span layoutId="tabpill" className="absolute inset-0 rounded-full bg-electric" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
                <span className={`relative ${tab === t.k ? "text-white" : "text-ice/70"}`}>{t.l}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-12 pb-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          {tab === "locations"
            ? <Locations locations={locations} />
            : tab === "bundles"
            ? <Bundles bundles={bundles} locations={locations} onBuy={buyBundle} onSingle={(id) => nav(`/book/${id}`)} />
            : <OneToOne />}
        </div>
      </section>

      <section className="relative pb-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8"><CommunitySignup locations={locations} /></div>
      </section>

      <BookingModal open={modal.open} onClose={closeModal} mode={modal.mode} slot={modal.slot} location={modal.location} bundle={modal.bundle} />

      <AnimatePresence>
        {showUpsell && (
          <motion.button data-testid="bundle-upsell" onClick={() => { setTab("bundles"); setShowUpsell(false); window.scrollTo({ top: 300, behavior: "smooth" }); }}
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[140] flex items-center gap-3 rounded-full glass px-6 py-3.5 text-ice hover:text-white glow-blue">
            <Tag size={16} className="text-cyan" /> Save with a bundle — see offers <ArrowRight size={16} />
          </motion.button>
        )}
      </AnimatePresence>
    </Layout>
  );
}
